import { useEffect, useState } from 'react';
import { AttachmentsService } from '@/services/AttachmentsService';
import type { GalleryImage } from '@/types/attachment';

/**
 * Shared attachments cache. One `/api/attachments` fetch per process,
 * populated lazily the first time any consumer asks for a thumbnail or full
 * record. Subsequent lookups hit the in-memory map.
 *
 * If the initial fetch fails (e.g. storage not configured), consumers resolve
 * to `null` and we retry on the next reload.
 */

const cache: Map<string, string | null> = new Map();
const fullCache: Map<string, GalleryImage> = new Map();
const listeners: Map<string, Set<(url: string | null) => void>> = new Map();
const fullListeners: Map<string, Set<(item: GalleryImage | null) => void>> = new Map();
let pending: Promise<void> | null = null;

// Lazy-init to avoid an ESM circular-dep init error with AttachmentsService,
// which imports invalidateAttachmentCache from this module.
let svc: AttachmentsService | null = null;
function getSvc(): AttachmentsService {
  if (!svc) svc = new AttachmentsService();
  return svc;
}

function ensureList(): Promise<void> {
  if (pending) return pending;
  pending = getSvc()
    .list()
    .then((items) => {
      for (const item of items) {
        const url = item.thumbnail ?? item.url ?? null;
        cache.set(item.id, url);
        fullCache.set(item.id, item);
        const ls = listeners.get(item.id);
        if (ls) ls.forEach((fn) => fn(url));
        const fls = fullListeners.get(item.id);
        if (fls) fls.forEach((fn) => fn(item));
      }
    })
    .catch(() => {
      pending = null;
    });
  return pending;
}

/**
 * @returns the best thumbnail URL for the given attachment id, or `null` when
 * unknown / not yet loaded / unavailable.
 */
export function useAttachmentThumbnail(id: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => (id ? cache.get(id) ?? null : null));

  useEffect(() => {
    if (!id) {
      setUrl(null);
      return;
    }
    if (cache.has(id)) {
      setUrl(cache.get(id) ?? null);
      return;
    }
    if (!listeners.has(id)) listeners.set(id, new Set());
    const fn = (u: string | null) => setUrl(u);
    listeners.get(id)!.add(fn);
    void ensureList();
    return () => {
      listeners.get(id)?.delete(fn);
    };
  }, [id]);

  return url;
}

/**
 * Clear the shared attachments cache and trigger a fresh fetch. Call this
 * after an upload / update / delete so components asking for newly-known ids
 * see them without a page reload. Existing listeners are re-notified as the
 * new list arrives.
 */
export function invalidateAttachmentCache(): void {
  cache.clear();
  fullCache.clear();
  pending = null;
  void ensureList();
}

/**
 * @returns the full attachment record for the given id, or `null` when
 * unknown / not yet loaded.
 */
export function useAttachmentById(id: string | null | undefined): GalleryImage | null {
  const [item, setItem] = useState<GalleryImage | null>(() => (id ? fullCache.get(id) ?? null : null));

  useEffect(() => {
    if (!id) {
      setItem(null);
      return;
    }
    if (fullCache.has(id)) {
      setItem(fullCache.get(id) ?? null);
      return;
    }
    if (!fullListeners.has(id)) fullListeners.set(id, new Set());
    const fn = (i: GalleryImage | null) => setItem(i);
    fullListeners.get(id)!.add(fn);
    void ensureList();
    return () => {
      fullListeners.get(id)?.delete(fn);
    };
  }, [id]);

  return item;
}

/** Test-only reset hook. */
export function __resetAttachmentThumbnailCache(): void {
  cache.clear();
  fullCache.clear();
  listeners.clear();
  fullListeners.clear();
  pending = null;
}
